import type { CreateEnquirySessionPayload } from 'api/accommodationBooking'
import { floatify } from 'lib/maths/mathUtils'

function getFlightOverheadPrice(rate: App.EnrichedPropertyRate): number {
  if (typeof rate.cheapestFlightPrice === 'number') return rate.cheapestFlightPrice
  return 0
}

function getHotelPricing(rate: App.EnrichedPropertyRate): CreateEnquirySessionPayload['pricing'] {
  const flightOverheadPrice = getFlightOverheadPrice(rate)
  const [hotelSellingPrice, hotelLuxPlusSellingPrice] = rate.bundle ? [
    rate.bundle.hotelSellingPrice,
    rate.bundle.hotelLuxPlusSellingPrice || undefined,
  ] : [
    floatify(rate.totals.sellingPrice - flightOverheadPrice),
    rate.luxPlusTotals?.sellingPrice
      ? floatify(rate.luxPlusTotals.sellingPrice - flightOverheadPrice)
      : undefined,
  ]

  const propertyFees = rate.totals.propertyFees || undefined
  // Contract: luxPlusTotal is exclusive of property fees, matching sellTotal.
  const luxPlusTotal = hotelLuxPlusSellingPrice

  return {
    currency: rate.currency,
    sellTotal: hotelSellingPrice,
    ...(propertyFees != null ? { propertyFees } : {}),
    ...(luxPlusTotal != null ? { luxPlusTotal } : {}),
  }
}

function mapOccupancy(occupancy: App.Occupants): CreateEnquirySessionPayload['occupancy'] {
  return {
    adults: occupancy.adults,
    ...(occupancy.children != null ? { children: occupancy.children } : {}),
    ...(occupancy.teenagers != null ? { teenagers: occupancy.teenagers } : {}),
    ...(occupancy.infants != null ? { infants: occupancy.infants } : {}),
    ...(occupancy.childrenAge?.length ? { childrenAges: occupancy.childrenAge } : {}),
  }
}

function mapSupplierMetaData(
  rate: App.EnrichedPropertyRate,
): CreateEnquirySessionPayload['supplierMetaData'] {
  if (rate.service.type === 'bedbank') {
    return {
      internalService: 'bedbank',
      selectedRate: {
        optionId: rate.optionId,
        internalRateId: rate.service.rateId,
        rateGroupId: rate.service.rateGroupId,
        bedGroupId: rate.service.bedGroups[0]?.id,
      },
      internalPropertyId: rate.service.propertyId,
      internalRoomId: rate.service.roomId,
    }
  }

  return {
    internalService: 'reservation',
    selectedRate: {
      optionId: rate.optionId,
      internalRateId: rate.service.rateId,
      ratePlanId: rate.service.ratePlanId,
      rateGroupId: rate.groupKey,
    },
    internalPropertyId: rate.service.propertyId,
    internalRoomId: rate.service.roomId,
  }
}

function mapSessionBundle(
  bundle: {
    bundleId: string
    tickets: Array<{ experienceId: string, productId: string, price: number }>
  },
  optIn?: true,
): NonNullable<CreateEnquirySessionPayload['bundle']> {
  return {
    bundleId: bundle.bundleId,
    tickets: bundle.tickets.map(({ experienceId, productId, price }) => ({
      experienceId,
      productId,
      ticketPrice: price,
    })),
    ...(optIn ? { optIn: true } : {}),
  }
}

export interface BuildEnquirySessionPayloadParams {
  rate: App.EnrichedPropertyRate
  occupancy: App.Occupants
  checkIn: string
  checkOut: string
  region: string
  selectedOptionalBundle?: Pick<App.Property.OptionalBundle, 'bundleId' | 'tickets'>
  /** Shopped LuxPlus+ eligibility. Checkout enquiry gates member-rate price changes on this. */
  isLuxPlusUser?: boolean
}

/**
 * Builds a svc-accommodation enquiry-session create payload from a unified property rate.
 * Rate identity uses optionId + supplier internalRateId — never the ephemeral unified rateId.
 *
 * Mandatory `rate.bundle` and a selected optional bundle are independent: both can sit on the
 * same session (`bundle` + `optInBundle`).
 */
export function buildEnquirySessionPayload({
  rate,
  occupancy,
  checkIn,
  checkOut,
  region,
  selectedOptionalBundle,
  isLuxPlusUser,
}: BuildEnquirySessionPayloadParams): CreateEnquirySessionPayload {
  const mandatoryBundle = rate.bundle

  return {
    pricing: getHotelPricing(rate),
    property: { propertyId: rate.propertyId },
    room: { roomId: rate.roomId },
    occupancy: mapOccupancy(occupancy),
    supplierMetaData: mapSupplierMetaData(rate),
    checkIn,
    checkOut,
    region,
    currency: rate.currency,
    ...(mandatoryBundle && {
      bundle: mapSessionBundle(mandatoryBundle),
    }),
    ...(selectedOptionalBundle && {
      optInBundle: mapSessionBundle(selectedOptionalBundle, true),
    }),
    ...(isLuxPlusUser && {
      createdContext: { userContext: { isLuxPlusUser: true } },
    }),
  }
}
